import React, { useRef } from 'react';
import styled from 'styled-components';
import StickyBox from 'react-sticky-box';

import { useCountry, useTranslation } from '@lt/localization';

// Helpers:
import { isSSR } from 'src/helpers/ssr';

// Components:
import { HotelCounter } from 'src/components/HotelCounter';
import HintFactory from 'src/components/Hint/HintFactory';
import SubscribeMobileMountains from 'src/components/SubscribeMobileMountains';
import EmptyHotels from '../Hotels/components/EmptyHotels';
import NearestOffers from '../EmptySearch/NearestOffers';
import MapContainer from '../Map';
import Subscribe from '../SubscriptionForm';
import { ModifierBanner } from '../ModifierBanner';

// Context:
import { MobileContext } from '../../domain/context/MobileContext';
import { LanguageContext } from '../../domain/context/LanguageContext';

// Containers:
import { HeaderContainer } from '../Header';
import { RecommendationContainer } from '../Recommendation';
import FilterContainer from '../FiltersSection';
import Sorting from '../Sorting';
import HotelsContainer from '../Hotels';
import { DepartureContainer } from '../DepartureSection';
import LinksSection from '../LinksSection';
import { PageDescription } from '../PageDescription';
import FloatingPanelMobile from '../FloatingPanelMobile';

import type { MainContainerProps } from './types';

export const MainContainer = (props: MainContainerProps): JSX.Element => {
  const {
    className = '',
    language = 'ru',
    is_mobile = false,
    fallbackSearch = false,
    hotelCounter,
    mapExpanded,
    hasHotels,
    nearestDeparturesData,
    nearestDatesData,
    modifierStyles,
    modifierBanner,
    searchType,
    isMetaregion,
    allowShowSubscription,
    hideFilters,
    isSubscribeShown,
  } = props;
  const isMobile = Boolean(is_mobile);
  const { t } = useTranslation();
  const { isRussia } = useCountry();
  const ref = useRef<HTMLDivElement>(null);

  const mapContainerMarginTop =
    (!isMobile &&
      (nearestDeparturesData || nearestDatesData || isMetaregion) &&
      '0') ||
    (!isMobile && fallbackSearch && '66') ||
    undefined;

  const showSubscribeWidget = allowShowSubscription && isRussia;
  const showEmptySection =
    fallbackSearch && !nearestDeparturesData && !nearestDatesData;
  const showNearestOffers = !!nearestDeparturesData || !!nearestDatesData;
  const showFloatingPanel =
    isMobile && !isSubscribeShown && hideFilters && !showEmptySection;

  return (
    <MobileContext.Provider value={isMobile}>
      <LanguageContext.Provider value={language}>
        <Container
          isMobile={isMobile}
          backgroundColor={modifierStyles.backgroundColor}
        >
          <HintFactory isMobile={isMobile} events class="react-hint" />

          <HeaderContainer />

          <Wrapper
            className={className}
            backgroundColor={modifierStyles.backgroundColor}
          >
            {modifierBanner ? (
              <ModifierBanner content={modifierBanner} isMobile={isMobile} />
            ) : null}

            <RecommendationContainer />

            <StyledMainSection ref={ref} isMobile={isMobile}>
              {showFloatingPanel && (
                <SubscribeMobileMountains>
                  <FloatingPanelMobile />
                </SubscribeMobileMountains>
              )}

              <StyledStickyBox isMobile={isMobile} offsetTop={70}>
                <Sidebar $isMobile={isMobile}>
                  {showSubscribeWidget && (
                    <StyledSubscribeWrapper isMobile={isMobile}>
                      <Subscribe />
                    </StyledSubscribeWrapper>
                  )}
                  <FilterContainer />

                  <DepartureContainer />
                </Sidebar>
              </StyledStickyBox>

              <StyledCenterSection>
                {showEmptySection ? (
                  <StyledEmptySection
                    mapExpanded={mapExpanded}
                    isMobile={isMobile}
                  >
                    <StyledSortingSection />

                    <EmptyHotels isMobile={isMobile} />
                  </StyledEmptySection>
                ) : null}

                <StyledTwoSection isMobile={isMobile}>
                  <StyledHotelSection
                    mapExpanded={mapExpanded}
                    isMobile={isMobile}
                  >
                    {showNearestOffers && (
                      <StyledNearestOffers
                        nearestDeparturesData={nearestDeparturesData}
                        nearestDatesData={nearestDatesData}
                        isMobile={isMobile}
                      />
                    )}
                    {isMetaregion && (
                      <LinksSection
                        isMetaregion
                        variant="countryList"
                        isMobile={isMobile}
                      />
                    )}

                    <StyledSortingSection>
                      {!hasHotels || fallbackSearch ? null : (
                        <>
                          <Sorting />
                          {isMobile ? null : (
                            <HotelCounter
                              hotelCounter={hotelCounter}
                              modifierStyles={modifierStyles}
                              searchType={searchType}
                            />
                          )}
                        </>
                      )}
                    </StyledSortingSection>

                    {fallbackSearch ? (
                      <StyledTitle isMobile={isMobile}>
                        {t('explore.hotels.alternatives')}
                      </StyledTitle>
                    ) : null}

                    <HotelsContainer />

                    <LinksSection
                      variant={isMetaregion ? 'resortList' : 'default'}
                      isMobile={isMobile}
                    />

                    <PageDescription />
                  </StyledHotelSection>

                  {isSSR ? null : (
                    <MapContainer marginTop={mapContainerMarginTop} />
                  )}
                </StyledTwoSection>
              </StyledCenterSection>
            </StyledMainSection>
          </Wrapper>
        </Container>
      </LanguageContext.Provider>
    </MobileContext.Provider>
  );
};

const Container = styled.div<{
  isMobile: boolean;
  backgroundColor?: string | null;
}>`
  background-color: ${({ backgroundColor }) => backgroundColor || '#e6e6e6'};
  min-width: ${({ isMobile }) => (isMobile ? 230 : 960)}px;
`;

const Wrapper = styled.div<{
  backgroundColor?: string | null;
}>`
  background-color: ${({ backgroundColor }) => backgroundColor || '#e6e6e6'};
  margin-top: 8px;
  padding: 0 8px;
`;

const StyledStickyBox = styled(StickyBox)<{ isMobile: boolean }>`
  height: fit-content;
  z-index: ${({ isMobile }) => (isMobile ? 900 : 'auto')};
`;

const StyledCenterSection = styled.div`
  width: 100%;
`;

const StyledEmptySection = styled.div<{
  isMobile: boolean;
  mapExpanded: boolean;
}>`
  ${({ isMobile }) =>
    !isMobile &&
    `
    padding-top: 28px;
    max-width: 700px;
  `}

  ${({ mapExpanded }) =>
    mapExpanded &&
    `
    min-width: 320px
    width: 320px;
    flex: 1 1 320px;
  `}
`;

const StyledTwoSection = styled.div<{
  isMobile: boolean;
}>`
  ${({ isMobile }) =>
    !isMobile &&
    `
    display: flex;
    flex-direction: row;
  `}
`;

const StyledHotelSection = styled.div<{
  isMobile: boolean;
  mapExpanded: boolean;
}>`
  ${({ isMobile }) =>
    !isMobile &&
    `
    max-width: 700px;
    flex: 1 1 700px;
  `}

  display: flex;
  flex-direction: column;

  ${({ mapExpanded }) =>
    mapExpanded &&
    `
    min-width: 320px
    width: 320px;
    flex: 1 1 320px;
  `}
`;

const StyledSortingSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const StyledMainSection = styled.div<{ isMobile: boolean }>`
  position: relative;
  margin-top: 24px;
  display: flex;
  flex-direction: row;

  ${({ isMobile }) =>
    isMobile &&
    `
    flex-direction: column-reverse;
    margin-top: 8px;
  `}
`;

const Sidebar = styled.div<{ $isMobile: boolean }>`
  width: 235px;
  min-width: 235px;
  padding-right: 15px;

  ${({ $isMobile }) =>
    $isMobile &&
    `
    & a,
    & button {
      font-size: 16px;
      line-height: 20px;
    }
  `}
`;

const StyledTitle = styled.div<{ isMobile: boolean }>`
  display: flex;

  font-weight: bold;
  font-size: 22px;
  line-height: 26px;
  margin: 24px 0 16px 1px;

  ${({ isMobile }) =>
    isMobile &&
    `
    font-size: 18px;
    line-height: 20px;
    margin: 16px 0 8px 1px;
  `}
`;

const StyledNearestOffers = styled(NearestOffers)<{
  isMobile: boolean;
}>`
  margin-bottom: 8px;

  ${({ isMobile }) =>
    isMobile &&
    `
    margin-bottom: 16px;
  `}
`;

const StyledSubscribeWrapper = styled.div<{ isMobile: boolean }>`
  ${({ isMobile }) =>
    !isMobile &&
    `
    margin-bottom: 16px;
  `}
`;
